'use client';
import React, { useEffect, useRef, useState } from 'react';

interface StatItem {
  value: string;
  numericEnd: number;
  prefix: string;
  suffix: string;
  label: string;
}

const stats: StatItem[] = [
  { value: '+120', numericEnd: 120, prefix: '+', suffix: '', label: 'Concesionarios activos' },
  { value: '$2.4B COP', numericEnd: 24, prefix: '$', suffix: '.4B COP', label: 'En inventario gestionado' },
  { value: '3 min', numericEnd: 3, prefix: '', suffix: ' min', label: 'Para publicar un vehículo' },
  { value: '94%', numericEnd: 94, prefix: '', suffix: '%', label: 'Tasa de retención' },
];

function CountUp({ end, prefix, suffix, trigger }: { end: number; prefix: string; suffix: string; trigger: boolean }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!trigger) return;
    let start = 0;
    const duration = 1800;
    const step = Math.ceil(end / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [trigger, end]);
  return <span>{prefix}{count}{suffix}</span>;
}

export default function DriveXHero() {
  const [line1Visible, setLine1Visible] = useState(false);
  const [line2Visible, setLine2Visible] = useState(false);
  const [subtitleVisible, setSubtitleVisible] = useState(false);
  const [ctaVisible, setCtaVisible] = useState(false);
  const [socialVisible, setSocialVisible] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t1 = setTimeout(() => setLine1Visible(true), 200);
    const t2 = setTimeout(() => setLine2Visible(true), 500);
    const t3 = setTimeout(() => setSubtitleVisible(true), 800);
    const t4 = setTimeout(() => setCtaVisible(true), 1000);
    const t5 = setTimeout(() => setSocialVisible(true), 1200);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); clearTimeout(t5); };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const logos = ['Automotores Chicamocha', 'SUV Premium BGA', 'Maverick Motors MDE', 'Moto Acuática Caribe'];

  return (
    <section
      id="hero"
      className="dx-font dx-grid-texture relative flex flex-col items-center justify-center text-center"
      style={{ minHeight: '100vh', paddingTop: '72px', background: '#0A0A0A' }}
    >
      <div className="max-w-4xl mx-auto px-6 flex flex-col items-center">
        {/* Eyebrow */}
        <div className="flex items-center gap-3 mb-8" style={{ opacity: line1Visible ? 1 : 0, transition: 'opacity 0.6s ease' }}>
          <span style={{ display: 'inline-block', width: '40px', height: '1px', background: '#C8102E', opacity: 0.5 }} />
          <span style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '10px', color: '#C8102E', letterSpacing: '0.35em', textTransform: 'uppercase' }}>
            PARA CONCESIONARIOS PREMIUM
          </span>
          <span style={{ display: 'inline-block', width: '40px', height: '1px', background: '#C8102E', opacity: 0.5 }} />
        </div>

        {/* H1 */}
        <h1 style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 900, fontSize: 'clamp(44px, 7.5vw, 92px)', letterSpacing: '-0.025em', textTransform: 'uppercase', lineHeight: 0.92, marginBottom: '28px' }}>
          <span
            className="block"
            style={{ color: '#FFFFFF', opacity: line1Visible ? 1 : 0, transform: line1Visible ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.6s ease 0.2s, transform 0.6s ease 0.2s' }}
          >
            El sistema operativo
          </span>
          <span
            className="block"
            style={{ color: '#C8102E', opacity: line2Visible ? 1 : 0, transform: line2Visible ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.6s ease 0.5s, transform 0.6s ease 0.5s' }}
          >
            de tu concesionario.
          </span>
        </h1>

        {/* Subtitle */}
        <p
          style={{
            fontFamily: 'Montserrat, sans-serif', fontWeight: 400, fontSize: '17px', color: '#AAAAAA',
            lineHeight: 1.75, maxWidth: '560px', marginBottom: '36px',
            opacity: subtitleVisible ? 1 : 0, transform: subtitleVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.6s ease 0.8s, transform 0.6s ease 0.8s'
          }}
        >
          Catálogo digital, pipeline de ventas, CRM y finanzas —<br />
          todo en una plataforma. Sin hojas de cálculo.<br />
          Sin herramientas fragmentadas.
        </p>

        {/* CTA Buttons */}
        <div
          className="flex flex-col sm:flex-row gap-4 mb-12"
          style={{ opacity: ctaVisible ? 1 : 0, transform: ctaVisible ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.6s ease 1s, transform 0.6s ease 1s' }}
        >
          <button
            onClick={() => scrollTo('pricing')}
            style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '13px', color: '#FFFFFF', background: '#C8102E', padding: '16px 36px', borderRadius: '4px', border: 'none', cursor: 'pointer', letterSpacing: '0.1em', textTransform: 'uppercase' }}
            onMouseEnter={e => ((e.currentTarget as HTMLElement).style.background = '#9B0D22')}
            onMouseLeave={e => ((e.currentTarget as HTMLElement).style.background = '#C8102E')}
          >
            Empezar — 14 días gratis
          </button>
          <button
            onClick={() => scrollTo('demo')}
            style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '13px', color: '#C8102E', background: 'transparent', padding: '16px 36px', borderRadius: '4px', border: '1px solid #C8102E', cursor: 'pointer', letterSpacing: '0.1em', textTransform: 'uppercase' }}
            onMouseEnter={e => ((e.currentTarget as HTMLElement).style.background = 'rgba(200,16,46,0.08)')}
            onMouseLeave={e => ((e.currentTarget as HTMLElement).style.background = 'transparent')}
          >
            Ver cómo funciona ↓
          </button>
        </div>

        {/* Social proof */}
        <div
          style={{ opacity: socialVisible ? 1 : 0, transform: socialVisible ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.6s ease 1.2s, transform 0.6s ease 1.2s' }}
          className="flex flex-col items-center gap-3 mb-16"
        >
          <span style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 500, fontSize: '11px', color: '#888888' }}>
            Ya usan DriveX:
          </span>
          <div className="flex flex-wrap justify-center gap-3">
            {logos.map((name) => (
              <div
                key={name}
                style={{
                  background: '#1A1A1A',
                  border: '1px solid #2A2A2A',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 600,
                  fontSize: '11px',
                  color: '#555555',
                  letterSpacing: '0.05em',
                  whiteSpace: 'nowrap',
                }}
              >
                {name}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div
        ref={statsRef}
        className="w-full"
        style={{ borderTop: '1px solid #2A2A2A' }}
      >
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4">
          {stats.map((stat, i) => (
            <div
              key={stat.label}
              className="flex flex-col items-center justify-center py-8 px-4"
              style={{ borderRight: i < stats.length - 1 ? '1px solid #2A2A2A' : 'none' }}
            >
              <span style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '32px', color: '#FFFFFF' }}>
                <CountUp end={stat.numericEnd} prefix={stat.prefix} suffix={stat.suffix} trigger={statsVisible} />
              </span>
              <span style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 500, fontSize: '11px', color: '#888888', textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: '4px', textAlign: 'center' }}>
                {stat.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
